import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { projectsAPI } from "../../lib/api";
import { getToken } from "../../lib/auth";
import { useAuth } from "../../contexts/AuthContext";
import { ProgressPosts } from "./ProgressPosts";
import { projectTypeLabel, PROJECT_STATUS_STYLES, ACCESS_VISIBLE_TYPES, formatAUDAmount, EditHistoryNote } from "../../lib/projects";
import { ArrowLeft, Save, Plus, Trash2, Mail, Copy, Ban, PauseCircle, PlayCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

export function ProjectDetail({ projectId, onBack }) {
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [invites, setInvites] = useState([]);
  const [fields, setFields] = useState({ description: "", cost: "", access_info: "" });
  const [payment, setPayment] = useState({ amount: "", date: "", note: "" });
  const [inviteForm, setInviteForm] = useState({ name: "", email: "" });
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await projectsAPI.getOne(projectId);
      setProject(res.data.project);
      setInvites(res.data.invites);
      setFields({
        description: res.data.project.description || "",
        cost: String(res.data.project.cost ?? ""),
        access_info: res.data.project.access_info || "",
      });
    } catch (err) {
      toast.error("Failed to load project");
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!project) return <p className="text-slate-500 py-10 text-center">Loading project...</p>;

  const showAccess = ACCESS_VISIBLE_TYPES.includes(project.project_type);
  const totalPaid = (project.payments || []).reduce((s, p) => s + (p.amount || 0), 0);

  const saveFields = async () => {
    try {
      await projectsAPI.update(projectId, {
        description: fields.description,
        cost: parseFloat(fields.cost) || 0,
        ...(showAccess ? { access_info: fields.access_info } : {}),
      });
      toast.success("Project updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No changes to save");
    }
  };

  const uploadPostFiles = async (files) => {
    const ids = [];
    for (const f of files || []) {
      const fd = new FormData();
      fd.append("file", f);
      const res = await projectsAPI.uploadPostFile(projectId, fd);
      ids.push(res.data.id);
    }
    return ids;
  };

  const addProgressPost = async (text, files) => {
    try {
      const ids = await uploadPostFiles(files);
      await projectsAPI.addProgressPost(projectId, text, ids);
      toast.success("Progress update added");
      load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add update");
      return false;
    }
  };

  const editProgressPost = async (postId, text, files, keptIds) => {
    try {
      const newIds = await uploadPostFiles(files);
      await projectsAPI.editProgressPost(projectId, postId, text, [...(keptIds || []), ...newIds]);
      toast.success("Progress note updated");
      load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update note");
      return false;
    }
  };

  const postFileUrl = (att, inline) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/post-files/${att.id}?inline=${inline ? "true" : "false"}`;

  const addPayment = async () => {
    if (!payment.amount || !payment.date) {
      toast.error("Enter amount and date");
      return;
    }
    try {
      await projectsAPI.addPayment(projectId, { amount: parseFloat(payment.amount), date: payment.date, note: payment.note });
      toast.success("Payment recorded");
      setPayment({ amount: "", date: "", note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add payment");
    }
  };

  const removePayment = async (p) => {
    if (!window.confirm(`Remove payment of ${formatAUDAmount(p.amount)}?`)) return;
    await projectsAPI.deletePayment(projectId, p.id);
    load();
  };

  const sendInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      toast.error("Enter name and email");
      return;
    }
    try {
      const res = await projectsAPI.invite(projectId, { ...inviteForm, origin: window.location.origin });
      setLastLink(res.data.portal_link);
      toast.success(`Invite created and emailed to ${inviteForm.email}`);
      setInviteForm({ name: "", email: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invite");
    }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke access for ${inv.name}?`)) return;
    await projectsAPI.revokeInvite(projectId, inv.id);
    toast.success("Access revoked");
    load();
  };

  const setStatus = async (status) => {
    if (status === "closed" && !window.confirm("Close this project? All external access will be revoked.")) return;
    if (status === "suspended" && !window.confirm("Suspend this project? External users will get read-only access.")) return;
    try {
      await projectsAPI.setStatus(projectId, status);
      toast.success(`Project marked as ${status}`);
      load();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/project-portal/${token}`);
    toast.success("Portal link copied");
  };

  return (
    <div className="space-y-6" data-testid="project-detail">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="project-back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{project.name}</h2>
            <p className="text-sm text-slate-500">{projectTypeLabel(project.project_type)}</p>
          </div>
          <Badge className={`${PROJECT_STATUS_STYLES[project.status]} hover:bg-opacity-100 capitalize`} data-testid="project-status-badge">{project.status}</Badge>
        </div>
        <div className="flex gap-2">
          {project.status !== "active" && (
            <Button variant="outline" size="sm" onClick={() => setStatus("active")} data-testid="project-reactivate-button">
              <PlayCircle className="w-4 h-4 mr-1 text-emerald-600" /> Reactivate
            </Button>
          )}
          {project.status === "active" && (
            <Button variant="outline" size="sm" onClick={() => setStatus("suspended")} data-testid="project-suspend-button">
              <PauseCircle className="w-4 h-4 mr-1 text-amber-600" /> Suspend
            </Button>
          )}
          {project.status !== "closed" && (
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setStatus("closed")} data-testid="project-close-button">
              <XCircle className="w-4 h-4 mr-1" /> Close Project
            </Button>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${showAccess ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Description</CardTitle></CardHeader>
          <CardContent>
            <Textarea rows={5} value={fields.description} onChange={(e) => setFields({ ...fields, description: e.target.value })} data-testid="project-description-input" />
            <EditHistoryNote history={project.edit_history} field="description" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost</CardTitle>
            <CardDescription>Agreed project cost</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
              <Input type="number" min="0" step="0.01" className="pl-7" value={fields.cost} onChange={(e) => setFields({ ...fields, cost: e.target.value })} data-testid="project-cost-input" />
            </div>
            <EditHistoryNote history={project.edit_history} field="cost" />
            <p className="text-xs text-slate-500 mt-3">Paid so far: <b>{formatAUDAmount(totalPaid)}</b> • Remaining: <b>{formatAUDAmount((parseFloat(fields.cost) || 0) - totalPaid)}</b></p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Payments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="space-y-1 max-h-36 overflow-y-auto" data-testid="project-payments-list">
              {(project.payments || []).length === 0 && <p className="text-sm text-slate-500">No payments recorded.</p>}
              {(project.payments || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                  <span>{new Date(p.date + "T00:00:00").toLocaleDateString("en-AU")} — <b>{formatAUDAmount(p.amount)}</b>{p.note ? ` (${p.note})` : ""}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => removePayment(p)} data-testid={`payment-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-1 pt-1 border-t">
              <Input type="number" min="0" step="0.01" placeholder="$" className="w-24 h-8 text-sm" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} data-testid="payment-amount-input" />
              <Input type="date" className="h-8 text-sm" value={payment.date} onChange={(e) => setPayment({ ...payment, date: e.target.value })} data-testid="payment-date-input" />
              <Button size="sm" className="h-8" onClick={addPayment} data-testid="payment-add-button"><Plus className="w-4 h-4" /></Button>
            </div>
          </CardContent>
        </Card>
        {showAccess && (
          <Card data-testid="project-access-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Access</CardTitle>
              <CardDescription>Site/hosting access details</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea rows={5} value={fields.access_info} onChange={(e) => setFields({ ...fields, access_info: e.target.value })} data-testid="project-access-input" />
              <EditHistoryNote history={project.edit_history} field="access_info" />
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={saveFields} className="bg-blue-600 hover:bg-blue-700" data-testid="project-save-fields-button">
          <Save className="w-4 h-4 mr-2" /> Save Project Details
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Progress Notes</CardTitle>
          <CardDescription>Each update is an individual post. You can only edit your own posts — everyone else's are read-only. All posts and edits are date/time stamped.</CardDescription>
        </CardHeader>
        <CardContent>
          <ProgressPosts
            posts={project.progress_posts || []}
            viewerKey={user ? `user:${user.username}` : null}
            readOnly={false}
            onAdd={addProgressPost}
            onEdit={editProgressPost}
            attachmentUrl={postFileUrl}
            authToken={getToken()}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">External Collaborators</CardTitle>
          <CardDescription>Invite people outside your organisation — they get a secure link to view this project and update progress (no CRM login, no access to costs/payments).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input placeholder="Name" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="invite-name-input" />
            <Input placeholder="Email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email-input" />
            <Button onClick={sendInvite} className="shrink-0" data-testid="invite-send-button">
              <Mail className="w-4 h-4 mr-1" /> Invite
            </Button>
          </div>
          {lastLink && (
            <p className="text-xs text-slate-600 bg-blue-50 rounded p-2 break-all" data-testid="invite-last-link">
              Portal link: {lastLink}
            </p>
          )}
          <div className="space-y-2" data-testid="invites-list">
            {invites.length === 0 && <p className="text-sm text-slate-500">No external users invited yet.</p>}
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <span className="text-sm font-medium text-slate-800">{inv.name}</span>
                  <span className="text-xs text-slate-500 ml-2">{inv.email}</span>
                  {inv.revoked && <Badge className="ml-2 bg-red-100 text-red-700 hover:bg-red-100">Revoked</Badge>}
                </div>
                {!inv.revoked && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyLink(inv.token)} title="Copy portal link" data-testid={`invite-copy-${inv.id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => revokeInvite(inv)} title="Revoke access" data-testid={`invite-revoke-${inv.id}`}>
                      <Ban className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
